import { useEffect, useMemo, useState } from 'react';
import { Table } from 'antd';
import Fuse from 'fuse.js';

import Search from '../assets/icons/search.svg';
import CrossIcon from '../assets/icons/cross-sign.svg';

const DataTable = ({
  removeContainerStyle,
  metadata,
  dataLoader,
  data,
  contentType,
  selectedRowKeys,
  setSelectedRowKeys,
  searchKeys,
  threshold = 0.28,
  // When true, use the enhanced name/email-centric search used by Member List
  memberSearch = false,
}) => {
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    dataLoader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setSearchValue('');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentType]);

  const onSelectChange = (selectedRowKeys) => {
    setSelectedRowKeys(selectedRowKeys);
  };

  const columns = metadata
    .map((item) => {
      return {
        title: item?.label,
        dataIndex: item?.name,
        ellipsis: item?.ellipsis,
        sorter: item?.sorter,
        sortDirections: item?.sortDirections,
        defaultSortOrder: item?.defaultSortOrder,
        align: item?.align,
        width: item?.width,
        render(text, record) {
          if (
            item?.name === 'event_sales_headline' ||
            item?.name === 'registration_url' ||
            item?.name === 'event_enrolled_headline'
          ) {
            return {
              children: (
                <div style={{ maxWidth: 150, whiteSpace: 'pre-wrap' }}>
                  {text}
                </div>
              ),
            };
          }

          return text;
        },
      };
    })
    .filter((i) => i.dataIndex !== 'key');

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
      //{
      //  key: 'odd',
      //  text: 'Select Odd Row',
      //  onSelect: changableRowKeys => {
      //    let newSelectedRowKeys = [];
      //    newSelectedRowKeys = changableRowKeys.filter((key, index) => {
      //      if (index % 2 !== 0) {
      //        return false;
      //      }
      //      return true;
      //    });
      //    setSelectedRowKeys(newSelectedRowKeys);
      //  },
      //},
      //{
      //  key: 'even',
      //  text: 'Select Even Row',
      //  onSelect: changableRowKeys => {
      //    let newSelectedRowKeys = [];
      //    newSelectedRowKeys = changableRowKeys.filter((key, index) => {
      //      if (index % 2 !== 0) {
      //        return true;
      //      }
      //      return false;
      //    });
      //    setSelectedRowKeys(newSelectedRowKeys);
      //  },
      //},
    ],
  };

  const tokenizeString = (str) => {
    return str
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .map((word) => word.trim());
  };

  // Helpers for enhanced member search
  const tokenizeEmail = (email) => {
    if (!email || typeof email !== 'string') return [];
    return email
      .toLowerCase()
      .trim()
      .split(/[@.+_-]+/)
      .filter((part) => part.length > 0);
  };

  const dataParser = (data) => {
    if (!data?.length) return [];

    // Helper to parse rows according to metadata
    const parseRows = (arr) => {
      const parsed = [];
      for (let i = 0; i < arr.length; i++) {
        parsed.push(
          metadata.reduce((map, attr) => {
            if (attr?.name) map[attr?.name] = attr.parser(arr[i]);
            return map;
          }, {})
        );
      }
      return parsed;
    };

    // If no search input or keys, just parse and return
    if (!searchKeys || !searchValue) return parseRows(data);

    // Member List enhanced search path (name/email centric and tolerant)
    if (memberSearch) {
      const query = searchValue.trim().toLowerCase();
      let filteredData = data;

      const looksLikeEmail = (q) => typeof q === 'string' && q.includes('@');

      if (looksLikeEmail(query)) {
        // email-specific ranking: exact > prefix > contains > fuzzy parts
        const exactEmail = data.filter(
          (item) => typeof item.email === 'string' && item.email.toLowerCase() === query
        );
        if (exactEmail.length) filteredData = exactEmail;
        else {
          const emailPrefix = data.filter(
            (item) => typeof item.email === 'string' && item.email.toLowerCase().startsWith(query)
          );
          if (emailPrefix.length) filteredData = emailPrefix;
          else {
            const emailContains = data.filter(
              (item) => typeof item.email === 'string' && item.email.toLowerCase().includes(query)
            );
            if (emailContains.length) filteredData = emailContains;
            else {
              const enhanced = data.map((item) => ({
                ...item,
                email_searchable: tokenizeEmail(item.email || '').join(' '),
              }));
              const fuse = new Fuse(enhanced, {
                keys: [
                  { name: 'email', weight: 2 },
                  { name: 'email_searchable', weight: 2 },
                ],
                threshold,
                ignoreLocation: true,
                minMatchCharLength: 2,
                includeScore: true,
              });
              filteredData = fuse.search(query).map((r) => r.item);
            }
          }
        }
      } else {
        // keyword mode over name/email with ranking + fallback fuzzy
        const tokens = tokenizeString(query);
        const mustContainAll = (str) => tokens.every((t) => str.includes(t));

        const exactName = data.filter(
          (item) => typeof item.name === 'string' && item.name.toLowerCase() === query
        );
        const prefixName = data.filter(
          (item) => typeof item.name === 'string' && item.name.toLowerCase().startsWith(query)
        );
        const containsAllTokens = data.filter((item) => {
          const name = (item.name || '').toLowerCase();
          const email = (item.email || '').toLowerCase();
          return mustContainAll(name) || mustContainAll(email);
        });

        const seen = new Set();
        const getId = (item) => item?.id ?? item?.key ?? item?.email ?? item?._id;
        const ranked = []
          .concat(exactName, prefixName, containsAllTokens)
          .filter((item) => {
            const ident = getId(item) ?? Symbol('unknown');
            if (seen.has(ident)) return false;
            seen.add(ident);
            return true;
          });

        if (ranked.length) filteredData = ranked;
        else {
          const enhanced = data.map((item) => ({
            ...item,
            email_searchable: tokenizeEmail(item.email || '').join(' '),
            name_searchable: tokenizeString(item.name || '').join(' '),
          }));
          const fuse = new Fuse(enhanced, {
            keys: [
              { name: 'name', weight: 2 },
              { name: 'name_searchable', weight: 2 },
              { name: 'email', weight: 1 },
              { name: 'email_searchable', weight: 1 },
            ],
            threshold,
            ignoreLocation: true,
            minMatchCharLength: 2,
            includeScore: true,
          });
          filteredData = fuse.search(tokens.join(' ')).map((r) => r.item);
        }
      }

      return parseRows(filteredData);
    }

    // Generic path: Fuse across parsed rows using provided searchKeys
    const parsed = parseRows(data);
    const normalizedSearchValue = tokenizeString(searchValue).join(' ');
    const returnData = new Fuse(parsed, {
      keys: searchKeys,
      threshold,
    });
    const results = returnData.search(normalizedSearchValue);
    return results.map((d) => d.item);
  };

  // Compute once per render to keep label and table consistent
  const dataMemo = useMemo(() => dataParser(data.data), [
    data.data,
    searchValue,
    searchKeys,
    memberSearch,
    metadata,
    threshold,
  ]);

  const SearchBox = () => (
    <div className="border border-gray-200 flex h-8 rounded-full flex justify-center items-center">
      <img src={Search} className="w-4 h-4 ml-4" alt="" />
      <input
        id="search"
        name="search"
        type="text"
        value={searchValue}
        className="text-sm px-2 rounded-full focus:outline-none"
        onChange={(e) => {
          setSearchValue(e.target.value);
        }}
      />
      {searchValue && (
        <img
          src={CrossIcon}
          className="w-3 h-4 mr-4 opacity-30"
          alt="clear search input"
          onClick={() => setSearchValue('')}
        />
      )}
    </div>
  );

  return (
    <div
      className={
        removeContainerStyle ? '' : 'bg-white p-5 mt-5 ttnk-table-container'
      }
    >
      <div className="flex justify-between items-center">
        <label className="m-3 font-semibold text-gray-400">
          Showing {dataMemo.length} result
          {dataMemo.length > 1 ? 's' : ''}
        </label>
        {data.count > 0 && searchKeys && SearchBox()}
      </div>
      <Table
        key={memberSearch ? `member-${searchValue || 'all'}` : `generic-${searchValue || 'all'}`}
        className="ttnk-table"
        pagination={false}
        columns={columns}
        loading={data.loading}
        rowKey={(record, index) =>
          record?.key ?? record?.id ?? record?._id ?? record?.email ?? index
        }
        dataSource={dataMemo}
      />
    </div>
  );
};

export default DataTable;
